/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import * as Yup from 'yup'

import Typography from 'gatsby-theme-leshen/src/components/Typography'
import { emilyFromConfig } from '../../services/Emily'
import { lapiFromConfig } from '../../services/LAPI'
import { tcpaFromConfig } from '../../services/TCPA'

import FormControl from './FormControl'
import { getFieldProps, getFieldValidationSchema } from './helpers'
import * as Styled from './LeadForm.styles'
import mapiUtils from './MAPIUtils'
import dangerouslyInitTrustedForm from './TrustedForm'

class LeadForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      ready: false,
      submitted: false,
      formData: {},
      initialValues: {},
      validationSchema: {},
    }

    this.trustedFormRef = React.createRef()

    this.formikRef = React.createRef()
    this.formRef = React.createRef()

    this.emily = emilyFromConfig()
    this.lapi = lapiFromConfig()
    this.tcpa = tcpaFromConfig()

    this.logError = this.logError.bind(this)
    this.loadFormData = this.loadFormData.bind(this)
    this.loadMAPIData = this.loadMAPIData.bind(this)
    this.initForm = this.initForm.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.submitTCPA = this.submitTCPA.bind(this)
    this.submitForm = this.submitForm.bind(this)
    this.closeForm = this.closeForm.bind(this)

    this.validationSchema = Yup.object().shape({
      xxTrustedFormToken: Yup.string().required('Required'),
      xxTrustedFormCertUrl: Yup.string().required('Required'),
    })
  }

  componentDidMount() {
    this.loadFormData()
  }

  componentWillUnmount() {
    window.removeEventListener(mapiUtils.readyEventName, this.loadMAPIData)
  }

  // eslint-disable-next-line react/sort-comp
  logError(message) {
    const formik = this.formikRef.current
    const referer = typeof window === 'undefined' ? '' : window.location.href
    const requestID =
      typeof formik.values === 'undefined' ? '' : formik.values.request_id
    this.lapi.error(message, referer, requestID)
  }

  loadFormData() {
    const { formGUID } = this.props
    this.emily
      .getForm(formGUID)
      .then(res => {
        const formData = res.data.body
        this.setState({ formData })

        dangerouslyInitTrustedForm(this.trustedFormRef.current)
          .then(({ token, certURL }) => {
            this.initForm(token, certURL)
          })
          .catch(() => {
            this.logError('failed to load tcpa fields')
          })
      })
      .catch(() => {
        this.logError('failed to load form fields')
      })
  }

  loadMAPIData() {
    const formik = this.formikRef.current
    if (formik) {
      mapiUtils.getRequestID(requestID => {
        formik.setFieldValue('request_id', requestID)
      })

      mapiUtils.getPromoCode(promoCode => {
        formik.setFieldValue('promo_code', promoCode)
      })
    }
  }

  initForm(xxTrustedFormToken, xxTrustedFormCertUrl) {
    const { formData } = this.state
    const { brandToken: brand, domain } = this.props

    const initialValues = {
      brand,
      domain,
      xxTrustedFormToken,
      xxTrustedFormCertUrl,
    }
    const shape = {}
    formData.elements.forEach(field => {
      if (field.inputType !== 'submit') {
        shape[field.htmlName] = getFieldValidationSchema(field)
      }
      initialValues[field.htmlName] = field.defaultValue || field.value || ''
    })
    const validationSchema = Yup.object().shape(shape)

    this.setState({ ready: true, initialValues, validationSchema }, () => {
      // Get a request id and promo code from MAPI.
      // If mapiUtils hasn't loaded yet, wait for the loaded event to fire.
      if (typeof window.mapiUtils === 'undefined') {
        window.addEventListener(mapiUtils.readyEventName, this.loadMAPIData)
      } else {
        this.loadMAPIData()
      }
    })
  }

  handleSubmit(values, actions) {
    const { errorMessage, successMessage } = this.props
    this.submitTCPA(values)
      .then(() => {
        this.submitForm(values)
          .then(() => {
            this.closeForm(successMessage, false, actions)
          })
          .catch(() => {
            this.logError('failed to submit lead to emily')
            this.closeForm(errorMessage, true, actions)
          })
      })
      .catch(() => {
        this.logError('failed to submit tcpa')
        this.closeForm(errorMessage, true, actions)
      })
  }

  submitTCPA(values) {
    let { phone } = values
    const { phone_number, brand, request_id, xxTrustedFormCertUrl } = values
    if (typeof phone === 'undefined') {
      phone = phone_number
    }
    return this.tcpa.createConsent({
      phone,
      brand,
      request_id,
      xxTrustedFormCertUrl,
    })
  }

  submitForm(values) {
    const { formData } = this.state
    return this.emily.submitForm({ ...values, action: formData.action })
  }

  // Each form submission needs to include a unique TCPA request.
  // Because the TrustedForm script gets loaded into the DOM during compnentDidMount, we would have to find the
  // TrustedForm script tag, remove it from the DOM, and then load it again to get new values.
  // It's safer and easier to simply hide the form and require the user to reload the page if they want to re-sumbit.
  closeForm(message, error, { setSubmitting, resetForm, setStatus }) {
    setSubmitting(false)
    resetForm()
    setStatus({ message, error })
    this.setState({ submitted: true })
  }

  render() {
    const {
      ready,
      submitted,
      formData,
      initialValues,
      validationSchema,
    } = this.state
    if (!ready) {
      return (
        <>
          <form ref={this.trustedFormRef} name="active-prospect">
            <input type="hidden" name="xxTrustedFormToken" />
            <input type="hidden" name="xxTrustedFormCertUrl" />
          </form>
          <Typography>Loading</Typography>
        </>
      )
    }

    const { as: Tag, labelType } = this.props

    return (
      <Formik
        ref={this.formikRef}
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        render={({
          values,
          touched,
          errors,
          handleSubmit,
          handleChange,
          handleBlur,
          status,
        }) => (
          <Tag ref={this.formRef} onSubmit={handleSubmit}>
            {status && (
              <Styled.ResultMessage hasError={status && status.error}>
                <Typography>{status && status.message}</Typography>
              </Styled.ResultMessage>
            )}
            {!submitted &&
              formData.elements.map(
                ({
                  name: label,
                  htmlName,
                  inputType,
                  options,
                  displayType,
                }) => (
                  <FormControl
                    id={htmlName}
                    key={htmlName}
                    name={htmlName}
                    value={values[htmlName]}
                    errorMessage={errors[htmlName]}
                    touched={touched[htmlName]}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    {...getFieldProps(
                      inputType,
                      label,
                      labelType,
                      displayType,
                      options
                    )}
                  />
                )
              )}
          </Tag>
        )}
      />
    )
  }
}

LeadForm.propTypes = {
  as: PropTypes.elementType,
  formGUID: PropTypes.string.isRequired,
  labelType: PropTypes.oneOf(['label', 'placeholder']),
  domain: PropTypes.string.isRequired,
  brandToken: PropTypes.string.isRequired,
  successMessage: PropTypes.string,
  errorMessage: PropTypes.string,
}

LeadForm.defaultProps = {
  as: Styled.Form,
  labelType: 'label',
  successMessage: 'Success!',
  errorMessage: 'An unknown error has occurred.',
}

export default LeadForm
